// @ts-nocheck
import React, { forwardRef, useEffect, useRef, useState } from "react";

import { Button, Form, Modal } from "react-bootstrap";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import Paginator from "./paginator/Paginator";
import "./TableTemplate.scss";

import { apiCallJson } from "../../Helpers";
import { accessProperty, getRandomNumber } from "../../Helpers/utils/utils";
import { hasValue } from "../../Helpers/validations/common";

const TableTemplate = forwardRef(
    (
        {
            title,
            subtitle,
            endpoint,
            dataSource,
            columns,
            uniqueId,
            addItem,
            onUpdate,
            onDelete,
            onFilter,
            onInfo,
            customButtons,
            customActions,
            extraFilters,
            clearExtraFiltersParams,
            paginator,
            pageSize,
            page,
            onSetPageSize,
            onSetCurrentPage,
            isMultiSelect,
            highlightValues,
            highlightKey,
            subInfo,
            defaultSort,
            showSizeSelector = true,
        },
        ref
    ) => {
        const actionsRef = useRef();
        const [t] = useTranslation();
        const location = useLocation();
        const [dataSourceState, setDataSourceState] = useState({
            count: 0,
            list: [],
        });
        const [firstTime, setFirstTime] = useState(true);
        const [pSize, setPSize] = useState(pageSize ? pageSize : 10);
        const [filters, setFilters] = useState({
            page: page ? page : 1,
            sortKey: defaultSort && hasValue(defaultSort.key) ? defaultSort.key : "",
            descending:
                defaultSort && hasValue(defaultSort.hasOwnProperty("descending")) ? defaultSort.descending : false,
        });
        const [advancedFilters, setAdvancedFilters] = useState({});
        const [deleteRow, setDeleteRow] = useState("");
        const [selectedRow, setSelectedRow] = useState("");
        const [multiselectRows, setMultiselectRows] = useState([]);
        const [showFilters, setShowFilters] = useState(false);
        const [showDeleteModal, setShowDeleteModal] = useState(false);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState(false);
        const [errorMessage, setErrorMessage] = useState([]);
        const [openMenus, setOpenMenus] = useState([]);
        const [subInfoOpen, setSubInfoOpen] = useState([]);

        //#region Effect Hooks
        useEffect(() => {
            const handler = (event) => {
                const target = event.target;
                if (openMenus && actionsRef.current && !actionsRef.current.contains(target)) {
                    setOpenMenus([]);
                }
            };

            document.addEventListener("mousedown", handler);
            document.addEventListener("touchstart", handler);

            return () => {
                document.removeEventListener("mousedown", handler);
                document.removeEventListener("touchstart", handler);
            };
        }, [openMenus]);

        useEffect(() => {
            if (dataSource) {
                setDataSourceState(dataSource);
            }
        }, [dataSource]);

        useEffect(() => {
            if (filters) {
                if (firstTime && dataSource) {
                    setDataSourceState(dataSource);
                    setFirstTime(false);
                    setLoading(false);
                    setError(false);
                    setErrorMessage([]);
                } else {
                    setFirstTime(false);
                    getItems();
                }
            }
        }, [filters, endpoint]);

        useEffect(() => {
            if (!firstTime) {
                setFilters({
                    ...filters,
                    page: 1,
                });
            }
        }, [pSize]);

        //#endregion

        //#region Handlers
        function handleSetPSize(pageSize) {
            if (onSetPageSize) {
                onSetPageSize(pageSize);
            }
            setPSize(pageSize);
        }

        function handlePageChange(newPage) {
            if (onSetCurrentPage) {
                onSetCurrentPage(newPage);
            }
            setMultiselectRows([]);
            if (loading === false) {
                setFilters({ ...filters, page: newPage });
            }
        }

        //#endregion

        const clearFilters = () => {
            setAdvancedFilters({});
            if (clearExtraFiltersParams) {
                clearExtraFiltersParams();
            }
            clearQueryParams();
            setFilters({
                page: 1,
                sortKey: "",
                descending: false,
            });
        };

        const clearQueryParams = () => {
            location.search = "";
        };

        const getItems = async () => {
            setLoading(true);

            let afQueryString = getFiltersQueryString();
            let efQueryString = getExtraFiltersQueryString();

            if (!endpoint.includes("?")) {
                endpoint += "?";
            } else {
                endpoint += "&";
            }

            const response = await apiCallJson(
                endpoint +
                    "pageNumber=" +
                    filters.page +
                    afQueryString +
                    efQueryString +
                    "&recordsPerPage=" +
                    pSize +
                    "&sortProp=" +
                    filters.sortKey +
                    "&descending=" +
                    filters.descending,
                "GET",
                null
            );
            if (response && response.success) {
                if (response.data.hasOwnProperty("list") && response.data.hasOwnProperty("count")) {
                    setDataSourceState(response.data);
                } else {
                    setDataSourceState({
                        list: response.data,
                        count: response.data.length,
                    });
                }
                setLoading(false);
                setError(false);
                setErrorMessage([]);
            } else if (response && !response.success) {
                setLoading(false);
                setError(true);
                setErrorMessage(response.errors);
            }
        };

        const getFiltersQueryString = () => {
            let filtersQueryString = Object.keys(advancedFilters)
                .filter((key) => advancedFilters[key] !== "" && advancedFilters[key] !== undefined)
                .map((key) => {
                    const col = columns.find((x) => x.name === key || x.filterName === key);
                    let value = advancedFilters[key];

                    if (col && col.filterParser) {
                        value = col.filterParser(value);
                    }

                    return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
                })
                .join("&");

            return hasValue(filtersQueryString) ? "&" + filtersQueryString : "";
        };

        const getExtraFiltersQueryString = () => {
            let extraQueryString = "";

            if (extraFilters) {
                extraQueryString = extraFilters
                    .filter((exFilter) => hasValue(exFilter.value))
                    .map((exFilter) => {
                        return `${encodeURIComponent(exFilter.name)}=${encodeURIComponent(exFilter.value)}`;
                    })
                    .join("&");
            }

            return hasValue(extraQueryString) ? "&" + extraQueryString : "";
        };

        const addEdit = (row) => {
            if (row[uniqueId] !== selectedRow[uniqueId]) {
                return (
                    <span
                        className="icon"
                        onClick={() => {
                            setSelectedRow({ ...row });
                        }}
                    >
                        edit
                    </span>
                );
            } else {
                return (
                    <span
                        className="icon"
                        onClick={() => {
                            onUpdate(selectedRow);
                            setSelectedRow("");
                        }}
                    >
                        save
                    </span>
                );
            }
        };

        const addButton = (button) => {
            return (
                <Button
                    key={button.name}
                    title={t(button.name)}
                    disabled={button.disabled}
                    className="button custom-button"
                    onClick={() => button.action(multiselectRows)}
                >
                    {button.icon ? (
                        <div className="icon">{button.icon}</div>
                    ) : (
                        <div className="name">{button.name}</div>
                    )}
                </Button>
            );
        };

        const addButtons = () => {
            return customButtons.map((x) => {
                if (x !== false) {
                    return addButton(x);
                }
            });
        };

        const setSortedkey = (column) => {
            const columnSortKey = hasValue(column.sortName) ? column.sortName : column.name;

            if (column.canSort && loading === false) {
                setFilters({
                    page: 1,
                    sortKey: columnSortKey,
                    descending: columnSortKey === filters.sortKey ? !filters.descending : false,
                });
            }
        };

        const getColumnContent = (row, column) => {
            if (column.canEdit && row[uniqueId] === selectedRow[uniqueId]) {
                return (
                    <Form.Control
                        value={selectedRow[column.name]}
                        onChange={(x) => {
                            setSelectedRow({ ...selectedRow, [column.name]: x.target.value });
                        }}
                    />
                );
            } else {
                if (column.caption) {
                    return <span className="row-caption">{column.caption(row)}</span>;
                } else {
                    const val = accessProperty(row, column.name);
                    return (
                        <span className="row-text" title={val}>
                            {val}
                        </span>
                    );
                }
            }
        };

        const sortButton = (column) => {
            const columnSortKey = hasValue(column.sortName) ? column.sortName : column.name;
            let sort;
            if (columnSortKey === filters.sortKey) {
                sort = filters.descending ? (
                    <span title={t("Descending")} className="icon">
                        south
                    </span>
                ) : (
                    <span title={t("Ascending")} className="icon">
                        north
                    </span>
                );
            } else {
                sort = <span className="void" />;
            }
            return sort;
        };

        const toggleSubInfoOpen = (i) => {
            if (subInfo && subInfo.displayOnlyOne) {
                setSubInfoOpen(subInfoOpen.includes(i) ? [] : [i]);
            } else {
                setSubInfoOpen(subInfoOpen.includes(i) ? subInfoOpen.filter((x) => x !== i) : [...subInfoOpen, i]);
            }
        };

        const renderFilters = () => {
            let filtersHtml = [];

            columns.forEach((column, i) => {
                if (column.canFilter) {
                    const filterKey = column.filterName ? column.filterName : column.name;

                    filtersHtml.push(
                        <div className="filter-cell" key={"filter-" + column.name}>
                            <div className="label">{column.headerCaption}</div>
                            <Form.Control
                                value={advancedFilters[filterKey] ? advancedFilters[filterKey] : ""}
                                onChange={(x) => {
                                    let value = x.target.value;
                                    if (x.target.value.startsWith(" ")) {
                                        value = x.target.value.replace(/^ /, "");
                                    }
                                    if (column.filterPattern && hasValue(value)) {
                                        if (column.filterPattern.test(value)) {
                                            setAdvancedFilters({
                                                ...advancedFilters,
                                                [filterKey]: value,
                                            });
                                        }
                                    } else {
                                        setAdvancedFilters({
                                            ...advancedFilters,
                                            [filterKey]: value,
                                        });
                                    }
                                }}
                            />
                        </div>
                    );
                }
            });

            if (extraFilters) {
                extraFilters.forEach((exFilter, i) => {
                    if (!exFilter.hide) {
                        filtersHtml.push(
                            <div className="filter-cell" key={"filter-" + exFilter.name}>
                                <div className="label">{exFilter.headerCaption}</div>
                                {exFilter.component}
                            </div>
                        );
                    }
                });
            }

            filtersHtml.push(
                <div className="filter-cell" key={"filter-apply-button"}>
                    <div className="label"></div>
                    <Button
                        title={t("ApplyFilters")}
                        className="button filter-button"
                        onClick={() => {
                            if (onFilter) {
                                onFilter({
                                    filters: getFiltersQueryString(),
                                    extraFilters: getExtraFiltersQueryString(),
                                });
                            }

                            setFilters({
                                ...filters,
                                page: 1,
                            });
                        }}
                    >
                        {t("ApplyFilters")}
                    </Button>
                </div>
            );

            return filtersHtml;
        };

        const renderHeaders = () => {
            let tableHeaders = [];

            if (isMultiSelect) {
                tableHeaders.push(
                    <div
                        key={"th-multiselect"}
                        className="is-multiselect"
                        onClick={() => {
                            if (multiselectRows.length === dataSourceState.list.length) {
                                setMultiselectRows([]);
                            } else {
                                setMultiselectRows([...dataSourceState.list]);
                            }
                        }}
                    >
                        {multiselectRows.length === dataSourceState.list.length && multiselectRows.length > 0
                            ? "check_box"
                            : multiselectRows.length === 0
                              ? "check_box_outline_blank"
                              : "indeterminate_check_box"}
                    </div>
                );
            }
            if (subInfo) {
                tableHeaders.push(<div key={"th-subinfo"} className="is-subinfo"></div>);
            }
            columns.forEach((column, i) => {
                if (column) {
                    const cSize = hasValue(column.size) ? column.size : "normal";
                    const cAlign = hasValue(column.textAlign) ? column.textAlign : "left";

                    tableHeaders.push(
                        <div
                            className={`header-cell align-${cAlign} cell-${cSize}`}
                            title={column.headerCaption}
                            // key={"th-" + column.name}
                        >
                            {sortButton(column)}
                            <span className={`header-text`} onClick={() => setSortedkey(column)}>
                                {column.headerCaption}
                            </span>
                        </div>
                    );
                }
            });

            if (checkIfActions()) {
                tableHeaders.push(<div className="header-cell-actions" key={"th-actions" + getRandomNumber()} />);
            }

            return tableHeaders;
        };

        const renderRows = () => {
            let rows = [];
            if (dataSourceState.list.length > 0) {
                dataSourceState.list.forEach((row, i) => {
                    const multiSelectIndexRow = multiselectRows.findIndex((x) => row[uniqueId] === x[uniqueId]);

                    let isHighlighted = false;

                    if (highlightKey && highlightValues) {
                        isHighlighted = highlightValues.includes(row[highlightKey]);
                    }

                    rows.push(
                        <div
                            key={"tr-" + i + "-" + row[uniqueId]}
                            className={`${isHighlighted ? "row-wrapper highlight" : "row-wrapper"}`}
                        >
                            {isMultiSelect && (
                                <div
                                    className="is-multiselect"
                                    onClick={() => {
                                        if (multiSelectIndexRow !== -1) {
                                            let newMulti = [...multiselectRows];
                                            newMulti.splice(multiSelectIndexRow, 1);
                                            setMultiselectRows(newMulti);
                                        } else {
                                            setMultiselectRows([...multiselectRows, row]);
                                        }
                                    }}
                                >
                                    {multiSelectIndexRow !== -1 ? "check_box" : "check_box_outline_blank"}
                                </div>
                            )}
                            {subInfo && subInfo.show && (
                                <div className="is-subinfo" onClick={() => toggleSubInfoOpen(i)}>
                                    {subInfoOpen.includes(i) ? "keyboard_arrow_down" : "keyboard_arrow_up"}
                                </div>
                            )}
                            {columns.map((column) => {
                                if (column) {
                                    const cSize = hasValue(column.size) ? column.size : "normal";
                                    const cAlign = hasValue(column.textAlign) ? column.textAlign : "left";
                                    return (
                                        <div
                                            className={`row-cell align-${cAlign} cell-${cSize}`}
                                            // key={`column-${i}-${column.name}`}
                                        >
                                            {getColumnContent(row, column)}
                                        </div>
                                    );
                                }
                            })}
                            {renderActions(row, i)}
                        </div>
                    );

                    if (subInfo && subInfo.show && subInfoOpen.includes(i)) {
                        rows.push(
                            <div key={"sub-info-component-" + getRandomNumber()} className="sub-info-component-bg">
                                <div className="sub-info-component-wrapper">{subInfo.subInfoElement(row, i)}</div>
                            </div>
                        );
                    }
                });
            } else {
                rows.push(
                    <div key={"empty"} className="row-empty">
                        {t("NoResultsInTable")}
                    </div>
                );
            }
            return rows;
        };

        const renderOverFlowActions = (openMenu) => {
            const rootHelperContainer = document.getElementById("root");
            const vScroll = window.pageYOffset;
            const postionX = openMenu.x - 170;
            const positionY = vScroll + openMenu.y;

            return rootHelperContainer
                ? ReactDOM.createPortal(
                      <div
                          key={openMenu.id + "-action-dropdown"}
                          className={`actions-dropdown show`}
                          style={{
                              position: "absolute",
                              zIndex: "4",
                              left: `${postionX}px`,
                              top: `${positionY}px`,
                          }}
                          ref={actionsRef}
                      >
                          {openMenu.actions.map((a, aix) => (
                              <div
                                  key={"action-" + aix}
                                  className="action-item"
                                  onClick={() => {
                                      toggleMenu(openMenu);
                                      a.action(openMenu.row);
                                  }}
                              >
                                  <div className="name">{t(a.name)}</div>
                              </div>
                          ))}
                      </div>,
                      rootHelperContainer
                  )
                : null;
        };

        const renderActions = (row, i) => {
            const actions = [];

            if (onInfo) {
                actions.push({
                    id: "info-" + i,
                    name: "Info",
                    action: () => {
                        onInfo(row);
                    },
                });
            }

            if (customActions) {
                customActions.map((x, idx) => {
                    const hideAction = x.hasOwnProperty("hide") && typeof x.hide === "function" ? x.hide(row) : false;
                    if (hideAction === false) {
                        actions.push({
                            id: "custom-" + i + "-" + idx,
                            name: typeof x.name === "function" ? x.name(row) : x.name,
                            action: (row) => {
                                x.action(row);
                            },
                        });
                    }
                });
            }

            if (onDelete) {
                actions.push({
                    id: "delete-" + i,
                    name: "Delete",
                    action: () => {
                        setDeleteRow(row);
                        setShowDeleteModal(true);
                    },
                });
            }

            return actions.length > 0 || onUpdate ? (
                <div className="row-cell-actions" key={"td-actions" + i + "-" + row[uniqueId]}>
                    {onUpdate && addEdit(row)}

                    {actions.length > 0 && (
                        <div
                            className="icon"
                            onClick={(e) => {
                                const rect = e.target.getBoundingClientRect();
                                toggleMenu({
                                    id: i,
                                    actions: actions,
                                    row: row,
                                    x: rect.x,
                                    y: rect.y,
                                });
                            }}
                        >
                            more_vert
                        </div>
                    )}
                </div>
            ) : (
                ""
            );
        };

        const toggleMenu = (openMenu) => {
            if (openMenus.length > 0) {
                setOpenMenus([]);
            } else {
                setOpenMenus([openMenu]);
            }
        };

        const getMultiSelectRows = () => {
            return multiselectRows;
        };

        const checkFiltersValue = () => {
            let hasValueExtraFilters = [];

            if (extraFilters) {
                hasValueExtraFilters = extraFilters.filter((ex) => !ex.hide && hasValue(ex.value));
            }

            const columnsToFilter = columns.filter(
                (column) =>
                    column.canFilter &&
                    (hasValue(advancedFilters[column.name]) || hasValue(advancedFilters[column.filterName]))
            );

            const allFilters = [...hasValueExtraFilters, ...columnsToFilter];
            return allFilters.length > 0;
        };

        const checkIfActions = () => {
            if (
                onInfo ||
                onDelete ||
                customActions?.filter((x) => {
                    if (x.hasOwnProperty("hide") && typeof x.hide === "function") {
                        return !x.hide(x);
                    }
                    return true;
                }).length > 0
            ) {
                return true;
            } else {
                return false;
            }
        };

        React.useImperativeHandle(ref, () => ({
            getItems: getItems,
            setAdvancedFilters: (extraFilters) => {
                setAdvancedFilters({
                    ...advancedFilters,
                    ...extraFilters,
                });
            },
            getMultiSelectRows: getMultiSelectRows,
        }));

        const renderInfoRow = (x, key) => {
            return (
                <div className="info-row" key={"info-" + getRandomNumber() + "-" + key}>
                    <div className="key">{x.key}:</div>
                    <div className="value">{hasValue(x.value) ? x.value : "-"}</div>
                </div>
            );
        };

        const hasExtraFilters = extraFilters ? extraFilters.filter((ex) => !ex.hide) : [];
        const columnsToFilter = columns ? columns.filter((column) => column.canFilter) : [];
        const filtersHaveValue = checkFiltersValue();
        const renderHeader = hasExtraFilters.length > 0 || columnsToFilter.length > 0 || title !== "" || addItem;

        return (
            <div className="table-template">
                {showDeleteModal && (
                    <Modal className="delete-modal" show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                        <Modal.Header className="delete-modal-head" closeButton>
                            <span className="title">{t("AreYouSureDelete")}</span>
                        </Modal.Header>
                        <Modal.Body className="delete-modal-body">
                            {columns.map((x) =>
                                renderInfoRow(
                                    {
                                        key: x.headerCaption,
                                        value: getColumnContent(deleteRow, x),
                                    },
                                    "delete-column"
                                )
                            )}
                            <div className="button-wrapper">
                                <Button className="button-cancel" onClick={() => setShowDeleteModal(false)}>
                                    {t("Cancel")}
                                </Button>
                                <Button
                                    className="delete-modal-btn"
                                    onClick={() => {
                                        onDelete(deleteRow);
                                        setShowDeleteModal(false);
                                    }}
                                >
                                    {t("Delete")}
                                </Button>
                            </div>
                        </Modal.Body>
                    </Modal>
                )}
                {renderHeader && (
                    <div className="header-wrapper">
                        <div className="title-wrapper">
                            {title && <h1 className="title">{title}</h1>}
                            {subtitle && <h2 className="subtitle">{subtitle}</h2>}
                        </div>
                        <div className="header-buttons-wrapper">
                            {(columnsToFilter.length > 0 || hasExtraFilters.length > 0) && (
                                <div
                                    className={showFilters ? "filters-button-wrapper active" : "filters-button-wrapper"}
                                    title={filtersHaveValue ? t("ThereAreFiltersApplied") : t("Filters")}
                                    onClick={() => {
                                        setShowFilters(!showFilters);
                                        if (showFilters) {
                                            // REMOVE FILTERS
                                            if (onFilter) {
                                                onFilter({
                                                    filters: "",
                                                    extraFilters: "",
                                                });
                                            }

                                            setTimeout(() => {
                                                clearFilters();
                                            }, 700);
                                        }
                                    }}
                                >
                                    <div className="filters-icon">{showFilters ? "filter_alt_off" : "filter_alt"}</div>
                                    {t("Filters")}
                                    {filtersHaveValue && (
                                        <div className="filters-alert" title={t("ThereAreFiltersApplied")} />
                                    )}
                                </div>
                            )}
                            {customButtons && addButtons()}
                            {addItem && (
                                <Button title={t("Add")} className="button add-button" onClick={() => addItem()}>
                                    <div className="icon">add</div>
                                </Button>
                            )}
                        </div>
                    </div>
                )}
                <div className={`filters-wrapper ${showFilters ? `show` : ``}`}>{renderFilters()}</div>
                {openMenus.map((op) => renderOverFlowActions(op))}
                <div className="table-wrapper">
                    <div className="table-header-wrapper">{renderHeaders()}</div>
                    <div className="body-wrapper">
                        {loading ? (
                            <div className="loader-container">
                                <div className="loader" />
                            </div>
                        ) : error ? (
                            <div className="error-container">
                                <div className="error" key={"error-title"}>
                                    {t("TableError")}
                                </div>
                                {errorMessage.map((e, i) => (
                                    <div className="error" key={"error-" + i}>
                                        {t(e.errorMessageKey)}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            renderRows()
                        )}
                    </div>
                </div>
                {paginator && dataSourceState.count !== 0 && (
                    <div className="paginator">
                        <Paginator
                            page={filters.page}
                            totalItems={dataSourceState.count}
                            showSizeSelector={showSizeSelector}
                            pageSize={pSize}
                            setPageSize={handleSetPSize}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>
        );
    }
);

export default TableTemplate;
